import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { CATEGORY_LABEL_KEY } from './justificationLabels';
import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select } from '../../design-system/primitives/Select/Select';
import { StaffPicker } from '../shared/StaffPicker';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  justificationsApi,
  type JustificationCategory,
  type JustificationRow,
  type StoreJustificationInput,
} from '../../lib/api/justifications';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './JustificationFormDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

const CATEGORIES: readonly JustificationCategory[] = ['missed_call', 'late_logs', 'other'];

/**
 * The file-a-complaint form — `POST /justifications` (M-11, Task 4, spec
 * §2, J-1).
 *
 * ## The subject is chosen by name, through `StaffPicker`
 *
 * It used to be a raw uuid `Input`, because `usersApi.list()` requires
 * `user.view`, which `justification.create` does not imply — and nobody
 * knows a colleague's uuid. `StaffPicker` reads `GET /lookups/staff`, which
 * `justification.create` DOES open (the API's `LookupPolicy`), so this
 * dialog's permission surface is still exactly `justification.create`. What
 * reaches the wire is unchanged: `subject_uuid`, the picked person's uuid.
 *
 * ## `subject_uuid` may not be the caller's own uuid (spec §2)
 *
 * `StoreJustificationRequest::withValidator()` refuses that with a `422`
 * naming `subject_uuid` (`errors.justification_self_complaint`) — this
 * dialog does not predict that refusal client-side; it renders whatever
 * message the server sends back, the same `mapDetails` split every sibling
 * form dialog in this codebase uses.
 *
 * ## `category` is one of exactly three values (spec J-1)
 *
 * The legacy `<select>` never emitted a fourth — a `Select`, not free text,
 * for the same reason the wire type itself is a closed union.
 */

export interface JustificationFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (created: JustificationRow) => void;
}

interface FormState {
  subjectUuid: string;
  category: JustificationCategory;
  complaint: string;
  occurredAt: string;
}

const BLANK: FormState = { subjectUuid: '', category: 'missed_call', complaint: '', occurredAt: '' };

export function JustificationFormDialog({ open, onClose, onSaved }: JustificationFormDialogProps) {
  const { t } = useTranslation();

  const [state, setState] = useState<FormState>(BLANK);
  const [missingFields, setMissingFields] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const subjectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setState(BLANK);
    setMissingFields({});
    setFieldErrors({});
    setFailure(null);
  }, [open]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('justifications.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('justifications.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('justifications.createFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    const missing: Record<string, boolean> = {};
    if (state.subjectUuid.trim() === '') missing['subjectUuid'] = true;
    if (state.complaint.trim() === '') missing['complaint'] = true;
    if (state.occurredAt.trim() === '') missing['occurredAt'] = true;

    if (Object.keys(missing).length > 0) {
      setMissingFields(missing);
      return;
    }
    setMissingFields({});

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setFailure(null);

    try {
      const input: StoreJustificationInput = {
        subject_uuid: state.subjectUuid.trim(),
        category: state.category,
        complaint: state.complaint.trim(),
        occurred_at: state.occurredAt,
      };

      const created = await justificationsApi.create(input);
      onSaved(created);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      kind="form"
      onClose={onClose}
      initialFocusRef={subjectRef}
      title={t('justifications.createTitle')}
    >
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <StaffPicker
          ref={subjectRef}
          label={t('justifications.subjectLabel')}
          width="fill"
          required
          value={state.subjectUuid === '' ? null : state.subjectUuid}
          error={missingFields['subjectUuid'] ? t('justifications.required') : fieldErrors['subject_uuid']}
          onChange={(uuid) => {
            setState((current) => ({ ...current, subjectUuid: uuid }));
          }}
        />

        <Select
          label={t('justifications.fieldCategory')}
          width="fill"
          required
          value={state.category}
          error={fieldErrors['category']}
          options={CATEGORIES.map((value) => ({ value, label: t(CATEGORY_LABEL_KEY[value]) }))}
          onChange={(event) => {
            const value = event.target.value as JustificationCategory;
            setState((current) => ({ ...current, category: value }));
          }}
        />

        <Input
          label={t('justifications.fieldOccurredAt')}
          width="fill"
          type="datetime-local"
          required
          value={state.occurredAt}
          error={missingFields['occurredAt'] ? t('justifications.required') : fieldErrors['occurred_at']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, occurredAt: value }));
          }}
        />

        <Textarea
          label={t('justifications.fieldComplaint')}
          dir="auto"
          value={state.complaint}
          error={missingFields['complaint'] ? t('justifications.required') : fieldErrors['complaint']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, complaint: value }));
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<FloppyDiskIcon size={16} />}>
            {t('justifications.createSubmit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('justifications.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
